import { useMemo } from "react";
import type { ComponentType } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Users, Building2, Clock, ArrowRight } from "lucide-react";
import { useTRPC } from "@/trpc";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type OrgLessHomeProps = {
  sharedBy?: string;
};

export function OrgLessHome({ sharedBy }: OrgLessHomeProps) {
  const trpc = useTRPC();

  const { data: sharedProviders, isLoading: isSharedProvidersLoading } =
    useQuery(trpc.profile.list.queryOptions({ source: "direct-share" }));

  const { data: createRequest, isLoading: isCreateRequestLoading } = useQuery(
    trpc.organization.getOrganizationCreateRequest.queryOptions(),
  );

  const isLoading = isSharedProvidersLoading || isCreateRequestLoading;

  const shareCount = sharedProviders?.length ?? 0;
  const hasShares = shareCount > 0;
  const hasPendingRequest = !!createRequest;

  const matchedSharerName = useMemo(() => {
    if (!sharedBy || !sharedProviders) {
      return null;
    }
    const match = sharedProviders.find(
      (provider) => provider.userId === sharedBy,
    );
    if (!match?.isRevealed) {
      return null;
    }
    const name = `${match.firstName ?? ""} ${match.lastName ?? ""}`.trim();
    return name || null;
  }, [sharedBy, sharedProviders]);

  const providerWord = shareCount === 1 ? "provider" : "providers";
  const profileWord = shareCount === 1 ? "their profile" : "their profiles";

  const sharedOption = {
    to: "/org/providers/shared" as const,
    icon: Users,
    title: "View shared providers",
    description: hasShares
      ? `${shareCount} ${providerWord} shared ${profileWord} with you`
      : "See profiles providers share with you",
    cta: "View providers",
    badge: matchedSharerName ? "New" : undefined,
    badgeTone: "primary" as const,
    iconTone: undefined,
  };

  const createOption = hasPendingRequest
    ? {
        to: "/org/create" as const,
        icon: Clock,
        title: "Organization request pending",
        description: `Your request for "${createRequest.name}" is in review — our team will contact you shortly.`,
        cta: "View request",
        badge: "Pending",
        badgeTone: "warning" as const,
        iconTone: "warning" as const,
      }
    : {
        to: "/org/create" as const,
        icon: Building2,
        title: "Create an organization",
        description:
          "Set up your organization to post jobs, manage credentials, and hire providers.",
        cta: "Get started",
        badge: undefined,
        badgeTone: "primary" as const,
        iconTone: undefined,
      };

  const emphasizeSharedFirst = hasPendingRequest || hasShares;

  const options = emphasizeSharedFirst
    ? [
        { ...sharedOption, emphasized: true },
        { ...createOption, emphasized: false },
      ]
    : [
        { ...createOption, emphasized: true },
        { ...sharedOption, emphasized: false },
      ];

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-16">
      <header className="flex items-start gap-4 sm:gap-5">
        {!isLoading && matchedSharerName && (
          <Avatar className="hidden h-14 w-14 shrink-0 border border-primary/15 sm:flex">
            <AvatarFallback className="bg-primary/10 text-base font-semibold text-primary">
              {getInitials(matchedSharerName)}
            </AvatarFallback>
          </Avatar>
        )}
        <div className="min-w-0 space-y-2">
          {isLoading ? (
            <>
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-9 w-72 max-w-full" />
              <Skeleton className="h-5 w-96 max-w-full" />
            </>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary/70">
                {hasShares || matchedSharerName ? "Shared with you" : "Welcome"}
              </p>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {matchedSharerName ? (
                  <>
                    <span className="text-primary">{matchedSharerName}</span>{" "}
                    shared their profile with you
                  </>
                ) : hasShares ? (
                  <>
                    {shareCount} {providerWord} shared {profileWord} with you
                  </>
                ) : (
                  "Welcome to Saile"
                )}
              </h1>
              <p className="text-sm text-slate-600 sm:text-base">
                {matchedSharerName || hasShares
                  ? hasPendingRequest
                    ? "Review the profiles shared with you — your organization request is in review."
                    : "Review the profiles shared with you, or set up your organization to start hiring."
                  : hasPendingRequest
                    ? "Your organization request is in review — meanwhile, you can view profiles providers share with you."
                    : "Set up your organization to post jobs and manage providers — or view profiles providers share with you."}
              </p>
            </>
          )}
        </div>
      </header>

      <div className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 sm:gap-5">
        {isLoading ? (
          <>
            <OptionCardSkeleton />
            <OptionCardSkeleton />
          </>
        ) : (
          options.map((option) => (
            <OptionCard
              key={option.to}
              to={option.to}
              icon={option.icon}
              title={option.title}
              description={option.description}
              cta={option.cta}
              badge={option.badge}
              badgeTone={option.badgeTone}
              iconTone={option.iconTone}
              emphasized={option.emphasized}
            />
          ))
        )}
      </div>
    </div>
  );
}

function OptionCard({
  to,
  icon: Icon,
  title,
  description,
  cta,
  badge,
  badgeTone = "primary",
  iconTone,
  emphasized,
}: {
  to: "/org/providers/shared" | "/org/create";
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  cta: string;
  badge?: string;
  badgeTone?: "primary" | "warning";
  iconTone?: "primary" | "muted" | "warning";
  emphasized: boolean;
}) {
  const resolvedIconTone = iconTone ?? (emphasized ? "primary" : "muted");

  return (
    <Link
      to={to}
      className={cn(
        "group relative flex h-full flex-col rounded-2xl border bg-white p-5 transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:p-6",
        "motion-safe:hover:-translate-y-0.5",
        emphasized
          ? "border-2 border-primary shadow-lg hover:shadow-xl"
          : "border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md",
      )}
    >
      {badge && (
        <span
          className={cn(
            "absolute -top-2.5 right-5 inline-flex h-5 items-center justify-center rounded-full px-2 text-xs font-semibold shadow-sm",
            badgeTone === "warning"
              ? "bg-amber-100 text-amber-800 ring-1 ring-amber-200"
              : "bg-primary text-primary-foreground",
          )}
        >
          {badge}
        </span>
      )}
      <span
        className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-colors",
          resolvedIconTone === "primary" &&
            "bg-primary text-primary-foreground",
          resolvedIconTone === "muted" &&
            "bg-slate-100 text-slate-600 group-hover:bg-slate-200",
          resolvedIconTone === "warning" &&
            "bg-amber-50 text-amber-600 group-hover:bg-amber-100",
        )}
      >
        <Icon className="h-6 w-6" />
      </span>
      <h2 className="mt-4 text-lg font-semibold text-slate-900">{title}</h2>
      <p className="mt-1.5 text-sm text-slate-600">{description}</p>
      <span
        className={cn(
          "mt-auto inline-flex items-center gap-1 pt-6 text-sm font-semibold",
          emphasized ? "text-primary" : "text-slate-500",
        )}
      >
        {cta}
        <ArrowRight
          aria-hidden="true"
          className="h-4 w-4 transition-transform motion-safe:group-hover:translate-x-1"
        />
      </span>
    </Link>
  );
}

function OptionCardSkeleton() {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <Skeleton className="h-12 w-12 shrink-0 rounded-xl" />
      <Skeleton className="mt-4 h-5 w-32" />
      <Skeleton className="mt-2 h-4 w-full max-w-[220px]" />
      <Skeleton className="mt-6 h-4 w-24" />
    </div>
  );
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
